'use client';

import {
  CubeIcon,
  BuildingsIcon,
  CaretUpDownIcon,
  ClipboardTextIcon,
  FileTextIcon,
  FlagIcon,
  FlaskIcon,
  GearIcon,
  LinkIcon,
  SignOutIcon,
  EnvelopeSimpleIcon,
  UserIcon,
  FlowArrowIcon,
  HeadCircuitIcon,
  DatabaseIcon,
  ClockIcon,
} from '@phosphor-icons/react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { T } from 'gt-react';
import { AuthContext } from '@/context/auth.context';
import { useContext, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import paths from '@/paths';
import CurrentUser from 'src/types/CurrentUser';
import { isAdmin } from '@/utils/user';
import { useViewMode } from '@/context/view-mode.context';
import { useCan } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';
import config from 'src/config';

function LabelledUserAvatar({
  currentUser,
}: {
  currentUser: CurrentUser | undefined;
}) {
  return (
    <>
      <Avatar className="h-8 w-8 rounded-lg">
        <AvatarImage src={undefined} alt={currentUser?.name} />
        <AvatarFallback className="rounded-lg">
          <UserIcon />
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
        <span className="truncate font-medium">{currentUser?.name}</span>
        <span className="truncate text-xs">{currentUser?.emails[0]}</span>
      </div>
    </>
  );
}

export function NavUser() {
  const { isMobile } = useSidebar();
  const { currentUser, logOut } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const navigate = useNavigate();
  const [canReadIntegrations] = useCan(Action.Read, Subject.Integration);
  const [canReadUserForOrg] = useCan(Action.Read, Subject.User);
  const [canReadUserGroupForOrg] = useCan(Action.Read, Subject.UserGroup);
  const [canReadApiKeyForOrg] = useCan(Action.Read, Subject.ApiKey);
  const canManageOrganization =
    isAdmin(currentUser, viewMode) ||
    canReadUserForOrg ||
    canReadUserGroupForOrg ||
    canReadApiKeyForOrg;
  const [canReadBackgroundAgents] = useCan(
    Action.Read,
    Subject.BackgroundAgent,
  );
  const [canReportSalesOrder] = useCan(Action.Report, Subject.SalesOrder);
  const [canReportSalesOrderDraft] = useCan(
    Action.Report,
    Subject.SalesOrderDraft,
  );
  const [canReportPurchaseOrder] = useCan(Action.Report, Subject.PurchaseOrder);
  const canReportAny =
    canReportSalesOrder || canReportSalesOrderDraft || canReportPurchaseOrder;
  const [openingControlPlane, setOpeningControlPlane] = useState(false);

  const isRestrictedUser = currentUser?.isRestrictedUser ?? false;

  const handleLogout = async () => {
    try {
      await logOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleOpenControlPlane = async () => {
    if (openingControlPlane) {
      return;
    }

    setOpeningControlPlane(true);
    let response: Response;

    try {
      response = await fetch(`${config.apiBaseUrl}/auth/control-plane`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Control plane handoff failed:', error);
      setOpeningControlPlane(false);
      return;
    }

    const payload = (await response.json().catch(() => null)) as {
      url?: string;
      message?: string;
    } | null;

    if (!response.ok || !payload?.url) {
      console.error(
        'Control plane handoff failed:',
        payload?.message ?? 'Unable to open control plane',
      );
      setOpeningControlPlane(false);
      return;
    }

    window.location.assign(payload.url);
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <LabelledUserAvatar currentUser={currentUser} />
              <CaretUpDownIcon className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <LabelledUserAvatar currentUser={currentUser} />
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() =>
                  navigate({ to: paths.authenticated.settings.user })
                }
              >
                <GearIcon />
                <T>User Preferences</T>
              </DropdownMenuItem>
              {!isRestrictedUser && canManageOrganization && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: paths.authenticated.settings.organization,
                      search: { section: 'general' },
                    })
                  }
                >
                  <BuildingsIcon />
                  <T>Manage Organization</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && currentUser?.tenantUser?.isAdmin && (
                <DropdownMenuItem
                  onClick={() => navigate({ to: paths.authenticated.tenant })}
                >
                  <BuildingsIcon />
                  <T>Manage Tenant</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({ to: paths.authenticated.documents })
                    }
                  >
                    <FileTextIcon />
                    <T>Documents</T>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({ to: paths.authenticated.inboundEmails })
                    }
                  >
                    <EnvelopeSimpleIcon />
                    <T>Inbound Emails</T>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem
                onClick={() =>
                  navigate({ to: paths.authenticated.sharedLinks })
                }
              >
                <LinkIcon />
                <T>Shared Links</T>
              </DropdownMenuItem>
              {!isRestrictedUser && canReadIntegrations && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.integrations.list })
                  }
                >
                  <CubeIcon />
                  <T>Integrations</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && canReportAny && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.auditReports })
                  }
                >
                  <ClipboardTextIcon />
                  <T>Audit Reports</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.workflows.list })
                  }
                >
                  <FlowArrowIcon />
                  <T>Workflows</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: paths.authenticated.workflows.scheduledReruns,
                    })
                  }
                >
                  <ClockIcon />
                  <T>Scheduled Reruns</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.backfills })
                  }
                >
                  <DatabaseIcon />
                  <T>Backfills</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser &&
                isAdmin(currentUser, viewMode) &&
                canReadBackgroundAgents && (
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({
                        to: paths.authenticated.backgroundAgents.list,
                      })
                    }
                  >
                    <HeadCircuitIcon />
                    <T>Background Agents</T>
                  </DropdownMenuItem>
                )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.evals.root })
                  }
                >
                  <FlaskIcon />
                  <T>Evals</T>
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  disabled={openingControlPlane}
                  onClick={handleOpenControlPlane}
                >
                  <CubeIcon />
                  {openingControlPlane ? (
                    <T>Opening Control Plane...</T>
                  ) : (
                    <T>Control Plane</T>
                  )}
                </DropdownMenuItem>
              )}
              {!isRestrictedUser && isAdmin(currentUser, viewMode) && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate({ to: paths.authenticated.featureFlags })
                  }
                >
                  <FlagIcon />
                  <T>Feature Flags</T>
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <SignOutIcon />
              <T>Log out</T>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
